import { useRef } from 'react';
import { EditorCrop } from './imageEditorCore';
import { CropHandle, CropRectangle } from './ImageEditorCrop.styled';

type Corner = 'nw' | 'ne' | 'sw' | 'se';
type Mode = Corner | 'move';

const CORNERS: Corner[] = ['nw', 'ne', 'sw', 'se'];

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/**
 * One gesture applied to the rect it started from - always from the ORIGIN
 * rect, never the current one, so a drag is stateless: the same pointer
 * position always produces the same rect, and there is no error to accumulate
 * across move events.
 */
const applyGesture = (
  origin: EditorCrop,
  mode: Mode,
  dx: number,
  dy: number,
  imageWidth: number,
  imageHeight: number,
  minSize: number
): EditorCrop => {
  if (mode === 'move') {
    return {
      ...origin,
      x: clamp(origin.x + dx, 0, imageWidth - origin.width),
      y: clamp(origin.y + dy, 0, imageHeight - origin.height),
    };
  }

  const right = origin.x + origin.width;
  const bottom = origin.y + origin.height;

  // Each corner moves its own two edges; the opposite two stay put.
  const x =
    mode === 'nw' || mode === 'sw'
      ? clamp(origin.x + dx, 0, right - minSize)
      : origin.x;
  const y =
    mode === 'nw' || mode === 'ne'
      ? clamp(origin.y + dy, 0, bottom - minSize)
      : origin.y;
  const newRight =
    mode === 'ne' || mode === 'se'
      ? clamp(right + dx, origin.x + minSize, imageWidth)
      : right;
  const newBottom =
    mode === 'sw' || mode === 'se'
      ? clamp(bottom + dy, origin.y + minSize, imageHeight)
      : bottom;

  return { x, y, width: newRight - x, height: newBottom - y };
};

type Props = {
  /** The rect being adjusted, in image pixels. */
  crop: EditorCrop;
  /** Display pixels per image pixel. */
  scale: number;
  imageWidth: number;
  imageHeight: number;
  /** Fired on every pointer move with the whole updated rect. */
  onChange: (crop: EditorCrop) => void;
  /** Fired once per finished gesture - the undo step boundary. */
  onCommit: () => void;
};

/**
 * The adjustable crop frame over the (full) picture while the crop tool is
 * active. Purely presentational plus pointer math: the rect itself lives with
 * the editor, which also decides when a rect means "no crop at all".
 */
export const ImageEditorCrop = ({
  crop,
  scale,
  imageWidth,
  imageHeight,
  onChange,
  onCommit,
}: Props) => {
  const gestureRef = useRef<{
    mode: Mode;
    startX: number;
    startY: number;
    origin: EditorCrop;
  } | null>(null);

  const begin = (mode: Mode) => (event: React.PointerEvent) => {
    // The canvas underneath must not also see this press.
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    gestureRef.current = {
      mode,
      startX: event.clientX,
      startY: event.clientY,
      origin: crop,
    };
  };

  const move = (event: React.PointerEvent) => {
    const gesture = gestureRef.current;
    if (!gesture) return;
    onChange(
      applyGesture(
        gesture.origin,
        gesture.mode,
        (event.clientX - gesture.startX) / scale,
        (event.clientY - gesture.startY) / scale,
        imageWidth,
        imageHeight,
        // Never smaller than 32 SCREEN px a side, whatever the zoom - an
        // image-pixel minimum would still collapse to a speck on a photo.
        32 / scale
      )
    );
  };

  const end = () => {
    if (!gestureRef.current) return;
    gestureRef.current = null;
    onCommit();
  };

  return (
    <CropRectangle
      style={{
        left: crop.x * scale,
        top: crop.y * scale,
        width: crop.width * scale,
        height: crop.height * scale,
      }}
      onPointerDown={begin('move')}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
    >
      {CORNERS.map((corner) => (
        <CropHandle
          key={corner}
          $corner={corner}
          onPointerDown={begin(corner)}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
        />
      ))}
    </CropRectangle>
  );
};
